import { useEffect, useRef, useState } from 'react';
import './App.css';
import { useWebSocket } from './hooks/useWebSocket';
import { ControlSidebar } from './components/ControlSidebar';
import { Calidad } from './types';
import AudioWave from './components/AudioWave';
import { Header } from './components/Header';
import ReactMarkdown from 'react-markdown';
import { Room, RoomEvent, Track, RemoteParticipant, RemoteTrackPublication } from 'livekit-client';

enum AvatarState {
  STOPPED = 'STOPPED',
  LOADING = 'LOADING',
  RUNNING = 'RUNNING'
}

type AvatarMode = 'video_loop' | 'liveavatar';

interface AvatarConfig {
  id: string;
  voice_id: string;
  quality: Calidad;
  mode: AvatarMode;
  system_prompt?: string;
}

interface LiveAvatarSession {
  session_id: string;
  livekit_url: string;
  livekit_client_token: string;
  ws_url?: string;
}

const getUserId = (): string => {
  let userId = localStorage.getItem('user_id');
  if (!userId) {
    userId = crypto.randomUUID();
    localStorage.setItem('user_id', userId);
  }
  return userId;
};

const markdownComponents: any = {
  a: ({ href, children }: { href?: string; children?: React.ReactNode }) => (
    <a href={href} target="_blank" rel="noopener noreferrer" className="underline text-blue-600 hover:text-blue-800">
      {children}
    </a>
  ),
};

function App() {
  const [avatarState, setAvatarState] = useState<AvatarState>(AvatarState.STOPPED);
  const [avatarMode, setAvatarMode] = useState<AvatarMode>('video_loop');
  const [userId] = useState<string>(getUserId());
  const [debug, setDebug] = useState<string>();
  const [showDebug, setShowDebug] = useState(false);
  const [microphonePermission, setMicrophonePermission] = useState<boolean | null>(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [configChanged, setConfigChanged] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(null);
  const [userTurn, setUserTurn] = useState(true);
  const audioChunks = useRef<Blob[]>([]);
  const [avatarConfig, setAvatarConfig] = useState<AvatarConfig | null>(null);
  const [generatedImageUrl, setGeneratedImageUrl] = useState<string | null>(null);
  const [isImageLoading, setIsImageLoading] = useState(false);
  const [responseComplete, setResponseComplete] = useState(false);
  const [isVoiceMode, setIsVoiceMode] = useState(true);
  const [textInput, setTextInput] = useState('');
  const [messages, setMessages] = useState<{ type: 'user' | 'assistant'; text: string }[]>([]);
  const [isChatVisible, setIsChatVisible] = useState(true);
  const [quality, setQuality] = useState<Calidad>(Calidad.HIGH);
  const [systemPrompt, setSystemPrompt] = useState<string>('');
  const [isMuted, setIsMuted] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false); // Florence está hablando

  const inputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const liveKitRoom = useRef<Room | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const audioQueueRef = useRef<string[]>([]); // cola de chunks de audio base64
  const isPlayingRef = useRef(false);
  const [isThinking, setIsThinking] = useState(false);
  // ── Audio ElevenLabs (modo video_loop) ────────────────────────────────────
  const playNextAudio = () => {
    if (isPlayingRef.current || audioQueueRef.current.length === 0) return;
    const audioB64 = audioQueueRef.current.shift()!;
    isPlayingRef.current = true;
    setIsSpeaking(true);

    const audio = new Audio(`data:audio/mpeg;base64,${audioB64}`);
    audio.volume = isMuted ? 0 : 1;
    audio.onended = () => {
      isPlayingRef.current = false;
      if (audioQueueRef.current.length > 0) {
        playNextAudio();
      } else {
        setIsSpeaking(false);
      }
    };
    audio.onerror = () => {
      isPlayingRef.current = false;
      setIsSpeaking(false);
      playNextAudio();
    };
    audio.play().catch(() => {
      isPlayingRef.current = false;
      setIsSpeaking(false);
    });
  };

  const enqueueAudio = (audioB64: string) => {
    audioQueueRef.current.push(audioB64);
    playNextAudio();
  };

  const clearAudioQueue = () => {
    audioQueueRef.current = [];
    isPlayingRef.current = false;
    setIsSpeaking(false);
  };

  // ── LiveKit (modo liveavatar) ──────────────────────────────────────────────
  const connectToLiveKit = async (session: LiveAvatarSession) => {
    try {
      if (liveKitRoom.current) await liveKitRoom.current.disconnect();
      const room = new Room();
      liveKitRoom.current = room;

      room.on(RoomEvent.TrackSubscribed, (track, _publication, _participant) => {
        if (track.kind === Track.Kind.Video) {
          const el = track.attach();
          if (videoRef.current) {
            videoRef.current.srcObject = (el as HTMLVideoElement).srcObject;
          }
        }
        if (track.kind === Track.Kind.Audio) {
          const audioEl = track.attach() as HTMLAudioElement;
          audioEl.volume = 1.0;
          document.body.appendChild(audioEl);
        }
      });
      room.on(RoomEvent.TrackUnsubscribed, (track) => { track.detach(); });
      room.on(RoomEvent.Disconnected, () => { setAvatarState(AvatarState.STOPPED); });

      await room.connect(session.livekit_url, session.livekit_client_token);

      room.remoteParticipants.forEach((participant: RemoteParticipant) => {
        participant.trackPublications.forEach((publication: RemoteTrackPublication) => {
          if (publication.track) {
            if (publication.track.kind === Track.Kind.Video && videoRef.current) {
              const el = publication.track.attach();
              videoRef.current.srcObject = (el as HTMLVideoElement).srcObject;
            }
            if (publication.track.kind === Track.Kind.Audio) {
              const audioEl = publication.track.attach() as HTMLAudioElement;
              audioEl.volume = 1.0;
              document.body.appendChild(audioEl);
            }
          }
        });
      });
      setAvatarState(AvatarState.RUNNING);
    } catch (error) {
      console.error('[LiveKit] Error conectando:', error);
      setAvatarState(AvatarState.STOPPED);
    }
  };

  const disconnectLiveKit = async () => {
    if (liveKitRoom.current) {
      await liveKitRoom.current.disconnect();
      liveKitRoom.current = null;
    }
    if (videoRef.current) videoRef.current.srcObject = null;
  };

  const cleanup = async () => {
    setGeneratedImageUrl(null);
    setIsImageLoading(false);
    clearAudioQueue();
    await disconnectLiveKit();
    if (mediaRecorder && mediaRecorder.state !== 'inactive') mediaRecorder.stop();
    setMediaRecorder(null);
    setIsRecording(false);
    setUserTurn(true);
    setResponseComplete(false);
    setAvatarState(AvatarState.STOPPED);
  };

  // ── WebSocket ─────────────────────────────────────────────────────────────
  const { sendMessage, sendBinaryMessage, wsRef, initWebSocket } = useWebSocket({
    onMessage: async (data) => {
      try {
        switch (data.type) {
          case 'initial_config': {
            const avatarData: AvatarConfig = data.data.avatar;
            const session: LiveAvatarSession | null = data.data.liveavatar_session;
            const mode: AvatarMode = avatarData.mode || 'video_loop';

            setAvatarConfig(avatarData);
            setAvatarMode(mode);
            setQuality(avatarData.quality as Calidad);
            setSystemPrompt(avatarData.system_prompt || '');

            sendMessage({
              sessionId: userId,
              heygenSessionId: session?.session_id || '',
              system_prompt: avatarData.system_prompt || undefined
            });

            if (mode === 'liveavatar' && session?.livekit_url && session?.livekit_client_token) {
              await connectToLiveKit(session);
            } else {
              // video_loop: simplemente marcamos como running
              setAvatarState(AvatarState.RUNNING);
            }
            setConfigChanged(false);
            break;
          }

          case 'audio_chunk': {
            // Solo en modo video_loop — audio de ElevenLabs
            if (data.audio && !isMuted) {
              enqueueAudio(data.audio);
            }
            break;
          }

          case 'image_loading':
            setIsImageLoading(data.loading);
            break;
          case 'thinking':
            setIsThinking(data.thinking);
            break;
          case 'generated_image':
            setIsImageLoading(false);
            setGeneratedImageUrl(data.format === 'base64' ? data.data : data.url);
            break;

          case 'transcription':
            if (data.text) {
              setMessages(prev => {
                const msgs = [...prev];
                for (let i = msgs.length - 1; i >= 0; i--) {
                  if (msgs[i].type === 'user') { msgs[i].text = data.text; break; }
                }
                return msgs;
              });
            }
            break;

          case 'text_chunk':
            if (data.text) {
              setMessages(prev => {
                const msgs = [...prev];
                const last = msgs[msgs.length - 1];
                if (!last || last.type !== 'assistant') {
                  msgs.push({ type: 'assistant', text: data.text });
                } else {
                  last.text += data.text;
                }
                return msgs;
              });
            }
            break;

          case 'response_complete':
            setResponseComplete(true);
            setUserTurn(true);
            break;

          case 'processing_error':
            setDebug(data.error);
            setUserTurn(true);
            break;

          case 'error':
            setDebug(data.error);
            setAvatarState(AvatarState.STOPPED);
            break;
        }
      } catch (error: any) {
        setDebug(error?.message || 'Error');
        setAvatarState(AvatarState.STOPPED);
      }
    },
    onError: () => { setAvatarState(AvatarState.STOPPED); },
    onClose: () => { if (avatarState !== AvatarState.STOPPED) setAvatarState(AvatarState.STOPPED); }
  });

  // ── Grabación ─────────────────────────────────────────────────────────────
  const startRecording = async () => {
    sendMessage({ type: "stop_audio" });
    clearAudioQueue();
    if (isRecording) { stopRecording(); return; }
    if (avatarState !== AvatarState.RUNNING || !microphonePermission) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      audioChunks.current = [];
      recorder.ondataavailable = (e) => { if (e.data.size > 0) audioChunks.current.push(e.data); };
      recorder.onstop = async () => {
        if (wsRef.current?.readyState === WebSocket.OPEN) {
          const audioBlob = new Blob(audioChunks.current);
          setUserTurn(false);
          setGeneratedImageUrl(null);
          setMessages(prev => [...prev, { type: 'user', text: 'Procesando audio...' }]);
          sendBinaryMessage(audioBlob);
        }
      };
      setMediaRecorder(recorder);
      recorder.start(100);
      setIsRecording(true);
    } catch {
      setDebug('Error accediendo al micrófono');
    }
  };

  const stopRecording = () => {
    if (mediaRecorder && mediaRecorder.state !== 'inactive') {
      mediaRecorder.stop();
      setIsRecording(false);
    }
  };

  // ── Texto ─────────────────────────────────────────────────────────────────
  const sendTextMessage = async () => {
    if (!textInput.trim() || avatarState !== AvatarState.RUNNING) return;
    sendMessage({ type: "stop_audio" });
    clearAudioQueue();
    await new Promise(resolve => setTimeout(resolve, 100));
    setMessages(prev => [...prev, { type: 'user', text: textInput }]);
    sendMessage({ type: "text", sessionId: userId, text: textInput });
    setUserTurn(false);
    setGeneratedImageUrl(null);
    setTextInput('');
  };

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleRestart = async () => {
    await cleanup();
    if (wsRef.current?.readyState === WebSocket.OPEN) wsRef.current.close();
    await new Promise(resolve => setTimeout(resolve, 500));
    initWebSocket();
  };
  const handleStop = async () => { await cleanup(); };
  const handleConfigChange = () => { if (avatarState !== AvatarState.STOPPED) setConfigChanged(true); };

  // ── Effects ───────────────────────────────────────────────────────────────
  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);
  useEffect(() => { if (!isVoiceMode && inputRef.current) inputRef.current.focus(); }, [isVoiceMode]);
  useEffect(() => {
    navigator.mediaDevices.getUserMedia({ audio: true })
      .then(() => setMicrophonePermission(true))
      .catch(() => setMicrophonePermission(false));
  }, []);

  // ── Guards ────────────────────────────────────────────────────────────────
  if (!avatarConfig && avatarState !== AvatarState.STOPPED) {
    return <div className="flex items-center justify-center h-screen">
      <span className="icon-[line-md--loading-loop] text-white w-8 h-8"></span>
    </div>;
  }

  if (avatarState === AvatarState.STOPPED && avatarConfig) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-gray-100">
        <div className="bg-white p-8 rounded-lg shadow-lg text-center max-w-md">
          <h2 className="text-2xl font-bold text-red-500 mb-4">Conexión perdida</h2>
          <p className="text-gray-700 mb-6">Reconectando, espere.</p>
          <button onClick={handleRestart} className="hidden px-6 py-3 bg-blue-500 text-white rounded-lg">Reconectar</button>
          {debug && showDebug && <div className="mt-4 p-3 bg-red-100 text-red-700 rounded text-sm">{debug}</div>}
        </div>
      </div>
    );
  }

  // ── Columna izquierda según modo ──────────────────────────────────────────
  const renderLeftColumn = () => {
    if (avatarMode === 'video_loop') {
      return (
        <video
          src="/listening.mp4"
          autoPlay
          loop
          muted
          playsInline
          className="w-full h-full object-contain max-h-[calc(100vh-100px)]"
          style={{ objectFit: 'scale-down' }}
        />
      );
    }
    // liveavatar: stream de LiveKit
    return (
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted={isMuted}
        className="w-full h-full object-contain max-h-[calc(100vh-100px)]"
        style={{ objectFit: 'scale-down' }}
      />
    );
  };

  // ── Render principal ──────────────────────────────────────────────────────
  return (
    <main className="flex flex-col h-screen">
      <Header onOpenSettings={() => setIsSidebarOpen(true)} />
      <section className='bg-white flex flex-col md:flex-row flex-1 relative h-screen'>

        {/* Columna izquierda — vídeo */}
        <div className="w-full h-[40vh] md:h-auto md:w-1/2 flex justify-center items-center relative"
          style={{ marginTop: '40px', maxHeight: 'calc(100vh - 50px)', display: 'flex', flexDirection: 'column' }}>
          {renderLeftColumn()}
          {(generatedImageUrl || isImageLoading) && (
            <div className="absolute inset-0 flex items-center justify-center p-4 pointer-events-none">
              <div className="relative w-full h-full max-w-4xl">
                {isImageLoading ? (
                  <div className="w-full h-full bg-black bg-opacity-20 rounded-lg flex items-center justify-center">
                    <span className="icon-[line-md--loading-loop] text-white w-16 h-16"></span>
                  </div>
                ) : (
                  <img src={generatedImageUrl!} alt="Imagen generada" className="w-full h-full object-contain" />
                )}
              </div>
            </div>
          )}
          {/* Indicador de que Florence está hablando (solo modo video_loop) */}
          {avatarMode === 'video_loop' && isSpeaking && (
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-50 text-white text-xs px-3 py-1 rounded-full">
              Florence está hablando...
            </div>
          )}
          {avatarMode === 'video_loop' && isThinking && !isSpeaking && (
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-60 text-white text-sm px-4 py-2 rounded-full flex items-center gap-2">
              <span className="animate-pulse">●</span>
              <span>Florence está pensando...</span>
            </div>
          )}
        </div>

        {/* Columna derecha — chat */}
        <div className="w-full h-[60vh] md:h-auto md:w-1/2 flex flex-col relative overflow-y-auto">
          <div className="w-full h-full p-2 md:p-4">
            <div className="bg-white rounded-lg shadow-lg p-4 flex flex-col h-full">
              <div className="flex justify-between items-center mb-2">
                <h3 className="text-lg font-semibold text-gray-700">Chat</h3>
                <button onClick={() => setIsChatVisible(!isChatVisible)} className="text-gray-500 hover:text-gray-700">
                  {isChatVisible ? 'Ocultar mensajes' : 'Mostrar mensajes'}
                </button>
              </div>

              {isChatVisible && (
                <div className="flex-1 overflow-y-auto mb-4" style={{ maxHeight: 'calc(100vh - 300px)' }}>
                  {messages.map((message, index) => (
                    <div key={index} className={`mb-4 ${message.type === 'user' ? 'text-right' : 'text-left'}`}>
                      <div className={`inline-block p-4 rounded-lg ${message.type === 'user' ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-800'}`}>
                        {message.type === 'assistant' ? (
                          <div className="prose prose-sm max-w-none dark:prose-invert">
                            <ReactMarkdown components={markdownComponents}>{message.text}</ReactMarkdown>
                          </div>
                        ) : message.text}
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </div>
              )}

              <div className="border-t pt-4">
                <div className="flex items-center mb-4 gap-4">
                  <div className="flex items-center gap-2">
                    <span className={`${!isVoiceMode ? "font-bold text-blue-500" : "text-gray-500"}`}>Texto</span>
                    <div onClick={() => setIsVoiceMode(!isVoiceMode)}
                      className="relative w-14 h-7 bg-gray-300 rounded-full cursor-pointer transition-colors duration-300">
                      <div className={`absolute w-5 h-5 bg-white rounded-full top-1 transition-transform duration-300 ${isVoiceMode ? "transform translate-x-8" : "translate-x-1"}`}></div>
                    </div>
                    <span className={`${isVoiceMode ? "font-bold text-blue-500" : "text-gray-500"}`}>Voz</span>
                  </div>
                </div>

                <div className="flex items-center justify-center">
                  {isVoiceMode ? (
                    <div className="text-center">
                      <div className="flex flex-col items-center">
                        <button
                          onClick={isRecording ? stopRecording : startRecording}
                          className={`px-4 py-2 rounded-full ${!microphonePermission ? 'bg-gray-500 cursor-not-allowed' : isRecording ? 'bg-red-500 hover:bg-red-600' : 'bg-blue-500 hover:bg-blue-600'} text-white transition-colors`}
                          disabled={!microphonePermission || avatarState !== AvatarState.RUNNING}
                        >
                          {isRecording
                            ? <div className="flex items-center"><span className="mr-2">⬤</span> Enviar respuesta</div>
                            : <div className="flex items-center"><span className="mr-2">🎤</span> Hablar con Florence</div>
                          }
                        </button>
                        <AudioWave isRecording={isRecording} />
                        <p className="text-xs sm:text-sm text-gray-600 mt-2">
                          {!microphonePermission ? 'Micrófono no disponible' : isRecording ? 'Grabando...' : 'Presiona el botón para hablar'}
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="w-full flex">
                      <input
                        ref={inputRef}
                        type="text"
                        value={textInput}
                        onChange={(e) => setTextInput(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && textInput.trim() && avatarState === AvatarState.RUNNING && sendTextMessage()}
                        placeholder="Escribe tu mensaje aquí..."
                        disabled={avatarState !== AvatarState.RUNNING}
                        className="flex-1 p-2 border rounded-l-lg focus:outline-none focus:ring-2 ring-blue-500"
                        autoFocus
                      />
                      <button onClick={sendTextMessage}
                        className="px-4 py-2 bg-blue-500 text-white rounded-r-lg hover:bg-blue-600 transition-colors disabled:bg-gray-400">
                        Enviar
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="absolute bottom-0 right-0 m-4 flex flex-col gap-y-4 items-end">
            {configChanged && (
              <p className="bg-gray-200 bg-opacity-75 text-red-500 text-xl font-bold p-4 rounded-lg shadow-md">
                Dale a "Reiniciar" para aplicar los cambios
              </p>
            )}
            <button onClick={handleRestart}
              className="bg-blue-500 text-white p-2 rounded-full shadow-lg hover:bg-blue-600 transition-colors"
              title="Reconectar">
              <span className="icon-[solar--restart-circle-bold] w-6 h-6"></span>
            </button>
          </div>

          <div className="absolute bottom-0 left-0 m-4 flex flex-col gap-y-4 items-start z-40">
            <button
              className={`bg-white bg-opacity-80 px-3 py-2 rounded shadow transition ${isMuted ? '' : 'opacity-70 hover:opacity-100'}`}
              onClick={() => setIsMuted(m => !m)}
            >
              {isMuted ? '🔇' : '🔊'}
            </button>
          </div>

          {debug && showDebug && (
            <div className="absolute bottom-0 left-0 m-4 p-2 bg-red-100 text-red-700 rounded">{debug}</div>
          )}
        </div>
      </section>

      <ControlSidebar
        isOpen={isSidebarOpen}
        onClose={() => setIsSidebarOpen(false)}
        onRestart={handleRestart}
        onStop={handleStop}
        calidad={quality}
        onCalidadChange={(q) => { setQuality(q); handleConfigChange(); }}
        systemPrompt={systemPrompt}
        onSystemPromptChange={(p) => { setSystemPrompt(p); handleConfigChange(); }}
        showDebug={showDebug}
        onShowDebugChange={setShowDebug}
      />
    </main>
  );
}

export default App;